// Dashboard v6 — Quiet cards, loud only on intent.
// Card resting: horse name + corner mark + progress bar.
// Hover: status line swaps for action chips (Log / View).
// Click: detail sheet opens; ← → cycle between horses in sort order.

import { useState, useMemo, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AVATAR_TONES } from "../lib/avatarTones";
import type { HorseColour } from "../lib/avatarTones";
import {
  computePace,
  computeFreshness,
  projectEndOfDay,
  paceFraction,
} from "../lib/hydration";
import type { CardState, PaceResult, Freshness } from "../lib/hydration";
import { useIsMobile } from "../lib/useMediaQuery";
import { useCurrentUser, firstName } from "../lib/useCurrentUser";
import EmailVerifyBanner from "../components/EmailVerifyBanner";
import { useUserYard } from "../lib/yard";
import { useHorses } from "../lib/horses";
import { signOut } from "../lib/auth";
import type { Horse } from "../lib/types";
import { MOCK_HORSES, MOCK_YARD, MOCK_NOW_HOUR } from "../lib/mockData";
import type { MockHorse } from "../lib/mockData";
import "../styles/dashboard.css";
import "../styles/dashboard-v6.css";

// Convert a Firestore Horse doc into the MockHorse shape that HorseCard /
// detail sheet / side rail know how to render. With no logs yet, the horse
// renders as "stale" (never logged) — accurate empty state.
function greetingFor(date: Date): string {
  const h = date.getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function horseToMock(h: Horse): MockHorse {
  const validColour = h.colour && (h.colour in AVATAR_TONES) ? (h.colour as HorseColour) : "bay";
  return {
    id: h.id,
    name: h.name,
    colour: validColour,
    breed: h.breed ?? "—",
    age: h.age ?? 0,
    weightKg: h.weightKg,
    waterL: 0,
    // Rough proxy until the first daily log produces a real EHE target.
    targetL: Math.max(20, Math.round(h.weightKg * 0.08)),
    lastLoggedMin: null,
    flags: {},
    history5: [null, null, null, null, null],
    today: null,
  };
}

interface CardStateResult {
  state: CardState;
  pace: PaceResult | null;
  fresh: Freshness;
  hasFlags: boolean;
}

function getCardState(horse: MockHorse): CardStateResult {
  const fresh = computeFreshness(horse.lastLoggedMin);
  if (fresh.kind === "stale") {
    return { state: "stale", pace: null, fresh, hasFlags: !!horse.flags?.combined };
  }
  const pace = computePace(horse.waterL, horse.targetL, MOCK_NOW_HOUR);
  const hasFlags = !!(horse.flags && (horse.flags.droppings || horse.flags.behaviour || horse.flags.combined));
  let state: CardState;
  if (pace.status === "severe" || pace.status === "critical" || horse.flags?.combined) state = "severe";
  else if (pace.status === "mild" || pace.status === "moderate" || hasFlags) state = "behind";
  else state = "ok";
  return { state, pace, fresh, hasFlags };
}

interface HorseMarkProps {
  horse: MockHorse;
  size?: number;
}

function HorseMark({ horse, size = 24 }: HorseMarkProps) {
  const tone = AVATAR_TONES[horse.colour] || "#5B6470";
  return (
    <span
      className="hm"
      style={{ background: tone, width: size, height: size, fontSize: size * 0.42 }}
      aria-hidden="true"
    >
      {horse.name[0]}
    </span>
  );
}

interface EnrichedHorse extends MockHorse {
  _state: CardState;
  _pace: PaceResult | null;
  _fresh: Freshness;
  _hasFlags: boolean;
}

interface HorseCardProps {
  horse: MockHorse;
  onOpen: (id: string) => void;
}

function HorseCard({ horse, onOpen }: HorseCardProps) {
  const navigate = useNavigate();
  const s = getCardState(horse);
  const pct = s.state === "stale" ? 0 : Math.min(100, (horse.waterL / horse.targetL) * 100);
  const expectedPct = s.pace ? Math.min(100, (s.pace.expectedL / horse.targetL) * 100) : 0;

  let subline: React.ReactNode;
  if (s.state === "stale") {
    const hrs = horse.lastLoggedMin == null ? null : Math.round(horse.lastLoggedMin / 60);
    subline = (
      <>
        <span className="sub-status stale">
          <svg className="sub-clock" width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.3" />
            <path d="M6 3.5V6l1.6 1.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
          {hrs ? `Not logged · ${hrs}h` : "No log yet today"}
        </span>
        {horse.lastKnownL && (
          <span className="sub-num mono stale-num">Last: {horse.lastKnownL}L</span>
        )}
      </>
    );
  } else {
    const deficit = s.pace ? Math.max(0, Math.round(s.pace.expectedL - horse.waterL)) : 0;
    const status = s.state === "ok" ? "On pace" : `${deficit}L behind`;
    subline = (
      <>
        <span className={`sub-status ${s.state}`}>{status}</span>
        <span className="sub-num mono">{horse.waterL}/{horse.targetL}L</span>
      </>
    );
  }

  return (
    <button
      className={`v6-card v6-card-${s.state}`}
      onClick={() => onOpen(horse.id)}
      aria-label={`${horse.name} — ${s.state}. Click for detail.`}
    >
      <header className="v6-card-head">
        <h3 className="v6-card-name">{horse.name}</h3>
        <HorseMark horse={horse} />
        {s.hasFlags && (
          <span className="v6-card-flag" title="Flag raised" aria-hidden="true">
            <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
              <path d="M3 1.5v9M3 1.5h6l-1.5 2L9 5.5H3" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
            </svg>
          </span>
        )}
      </header>

      <div
        className={`v6-bar v6-bar-${s.state}`}
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        {s.state !== "stale" && pct > 0 && (
          <span className="v6-bar-fill" style={{ width: `${pct}%` }} />
        )}
        {s.state !== "stale" && expectedPct > 1 && (
          <span className="v6-bar-tick" style={{ left: `${expectedPct}%` }} aria-hidden="true" />
        )}
        {s.state === "stale" && horse.lastKnownL && (
          <span
            className="v6-bar-memory"
            style={{ width: `${Math.min(100, (horse.lastKnownL / horse.targetL) * 100)}%` }}
            aria-label={`Yesterday: ${horse.lastKnownL}L`}
          />
        )}
      </div>

      <footer className="v6-card-foot">
        <div className="v6-card-sub">{subline}</div>
        <div className="v6-card-actions" onClick={(e) => e.stopPropagation()}>
          <span
            className="v6-act"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/horses/${horse.id}/log`);
            }}
          >
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M6 1.5v9M1.5 6h9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
            Log
          </span>
          <span
            className="v6-act"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/horses/${horse.id}`);
            }}
          >
            View
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M3 6h6M7 3l2.5 3L7 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </span>
        </div>
      </footer>
    </button>
  );
}

interface YardColumnProps {
  enriched: EnrichedHorse[];
  onPickHorse: (id: string) => void;
}

function YardColumn({ enriched, onPickHorse }: YardColumnProps) {
  const counts = useMemo(() => {
    const c = { ok: 0, behind: 0, severe: 0, stale: 0, total: enriched.length };
    enriched.forEach((h) => {
      c[h._state]++;
    });
    return c;
  }, [enriched]);

  const attnList = enriched
    .filter((h) => h._state === "severe" || h._state === "behind")
    .sort((a, b) => (a._state === "severe" ? -1 : 1));
  const staleList = enriched.filter((h) => h._state === "stale");
  const pct = Math.round(paceFraction(MOCK_NOW_HOUR) * 100);

  return (
    <aside className="v6-side">
      <header className="v6-side-head">
        <span className="v6-eyebrow">{MOCK_YARD.date} · 10:42 am</span>
        <h2>{MOCK_YARD.name}</h2>
        <p className="v6-side-meta">
          {enriched.length} horses · {pct}% through drinking day
        </p>
      </header>

      <section className="v6-pulse">
        <div className="v6-pulse-num">
          <span className="big mono">{counts.ok}</span>
          <span className="of">of {counts.total} on pace</span>
        </div>
        <div className="v6-pulse-bar">
          {counts.ok > 0 && <span className="seg ok" style={{ flex: counts.ok }} />}
          {counts.behind > 0 && <span className="seg behind" style={{ flex: counts.behind }} />}
          {counts.severe > 0 && <span className="seg severe" style={{ flex: counts.severe }} />}
          {counts.stale > 0 && <span className="seg stale" style={{ flex: counts.stale }} />}
        </div>
        <ul className="v6-pulse-key">
          <li><span className="key-mark ok"></span><span>On pace</span><span className="num mono">{counts.ok}</span></li>
          <li><span className="key-mark behind"></span><span>Behind pace</span><span className="num mono">{counts.behind}</span></li>
          <li><span className="key-mark severe"></span><span>Critical</span><span className="num mono">{counts.severe}</span></li>
          <li><span className="key-mark stale"></span><span>Not logged</span><span className="num mono">{counts.stale}</span></li>
        </ul>
      </section>

      {(attnList.length > 0 || staleList.length > 0) && (
        <section className="v6-attn">
          <h3 className="v6-attn-h">Needs your eyes</h3>
          <ul className="v6-attn-list">
            {attnList.map((h) => (
              <li key={h.id}>
                <button className="v6-attn-row" onClick={() => onPickHorse(h.id)}>
                  <HorseMark horse={h} size={20} />
                  <span className="v6-attn-name">{h.name}</span>
                  <span className="v6-attn-note">
                    {h.flags?.combined
                      ? "colic pattern"
                      : h._state === "severe"
                        ? `${Math.round(Math.max(0, (h._pace?.expectedL ?? 0) - h.waterL))}L behind`
                        : h.flags?.droppings
                          ? "droppings concern"
                          : h.flags?.behaviour
                            ? "behaviour"
                            : `${Math.round(Math.max(0, (h._pace?.expectedL ?? 0) - h.waterL))}L behind`}
                  </span>
                  <svg className="v6-attn-arrow" width="10" height="10" viewBox="0 0 12 12" fill="none">
                    <path d="M3 6h6M7 3l2.5 3L7 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </li>
            ))}
            {staleList.map((h) => (
              <li key={h.id}>
                <button className="v6-attn-row" onClick={() => onPickHorse(h.id)}>
                  <HorseMark horse={h} size={20} />
                  <span className="v6-attn-name">{h.name}</span>
                  <span className="v6-attn-note">
                    {h.lastLoggedMin == null
                      ? "no log yet"
                      : `${Math.round(h.lastLoggedMin / 60)}h since log`}
                  </span>
                  <svg className="v6-attn-arrow" width="10" height="10" viewBox="0 0 12 12" fill="none">
                    <path d="M3 6h6M7 3l2.5 3L7 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </aside>
  );
}

interface StatProps {
  lbl: string;
  val: string | number;
  unit?: string;
  sub: string;
}

function Stat({ lbl, val, unit, sub }: StatProps) {
  return (
    <div className="v6-stat">
      <span className="v6-stat-lbl">{lbl}</span>
      <span className="v6-stat-val mono">
        {val}
        {unit && <span className="unit">{unit}</span>}
      </span>
      <span className="v6-stat-sub">{sub}</span>
    </div>
  );
}

interface DetailOverlayProps {
  horse: MockHorse | null;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

function DetailOverlay({ horse, onClose, onPrev, onNext }: DetailOverlayProps) {
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, onPrev, onNext]);

  if (!horse) return null;
  const s = getCardState(horse);
  const pct = s.state === "stale" ? 0 : Math.min(100, (horse.waterL / horse.targetL) * 100);
  const expectedPct = s.pace ? Math.min(100, (s.pace.expectedL / horse.targetL) * 100) : 0;
  const deficit = s.pace ? Math.max(0, Math.round(s.pace.expectedL - horse.waterL)) : 0;
  const eod = projectEndOfDay(horse.waterL, MOCK_NOW_HOUR);
  const headline =
    s.state === "stale" ? "Not logged today"
    : s.state === "ok" ? "On pace for the day"
    : s.state === "behind" ? `Behind pace by ${deficit}L`
    : `Critically low: ${deficit}L behind`;

  return (
    <div className="v6-overlay" onClick={onClose}>
      <button
        className="v6-nav v6-nav-prev"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        aria-label="Previous horse"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12 5l-5 5 5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      <button
        className="v6-nav v6-nav-next"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        aria-label="Next horse"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M8 5l5 5-5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>

      <div className="v6-sheet" onClick={(e) => e.stopPropagation()}>
        <button className="v6-close" onClick={onClose} aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
        </button>

        <header className="v6-sheet-head">
          <div className="v6-sheet-id">
            <HorseMark horse={horse} size={36} />
            <div>
              <h2>{horse.name}</h2>
              <p className="muted">{horse.breed} · {horse.age}yo · {horse.weightKg}kg</p>
            </div>
          </div>
          <span className={`v6-state-pill state-${s.state}`}>{headline}</span>
        </header>

        <div className="v6-sheet-bar-wrap">
          <div className={`v6-bar v6-bar-big v6-bar-${s.state}`}>
            {s.state !== "stale" && pct > 0 && <span className="v6-bar-fill" style={{ width: `${pct}%` }} />}
            {s.state !== "stale" && expectedPct > 1 && (
              <span className="v6-bar-tick big" style={{ left: `${expectedPct}%` }} aria-hidden="true" />
            )}
          </div>
          <div className="v6-bar-legend">
            <span className="lhs">0L</span>
            <span className="rhs mono">{horse.targetL}L target</span>
          </div>
        </div>

        <div className="v6-stats">
          <Stat lbl="Today" val={horse.waterL} unit="L" sub="drunk so far" />
          <Stat
            lbl="Expected by now"
            val={s.pace ? Math.round(s.pace.expectedL) : "—"}
            unit={s.pace ? "L" : ""}
            sub={s.pace ? `${Math.round(s.pace.paceFrac * 100)}% through day` : ""}
          />
          <Stat
            lbl="Projected end"
            val={s.state === "stale" ? "—" : Math.round(eod)}
            unit={s.state === "stale" ? "" : "L"}
            sub="at current rate"
          />
        </div>

        {horse.flags && (horse.flags.combined || horse.flags.droppings || horse.flags.behaviour) && (
          <div className="v6-flag-block">
            <span className="v6-flag-eyebrow">Flag{horse.flags.combined ? "" : "s"} raised</span>
            <ul>
              {horse.flags.combined && <li>Colic pattern: low water plus abnormal droppings plus behaviour.</li>}
              {horse.flags.droppings && !horse.flags.combined && <li>Droppings: {horse.today?.droppings || "abnormal"}.</li>}
              {horse.flags.behaviour && !horse.flags.combined && <li>Behaviour: {horse.today?.behaviour || "unusual"}.</li>}
            </ul>
          </div>
        )}

        <section className="v6-history">
          <span className="v6-eyebrow">Last 5 days</span>
          <div className="v6-history-row">
            {horse.history5.map((status, i) => (
              <span key={i} className={`v6-history-cell ${status || "none"}`} title={status || "no data"} />
            ))}
          </div>
        </section>

        {horse.today && (
          <section className="v6-today">
            <span className="v6-eyebrow">Today's log</span>
            <dl>
              <div><dt>Behaviour</dt><dd>{horse.today.behaviour || "—"}</dd></div>
              <div><dt>Droppings</dt><dd>{horse.today.droppings || "—"} · {horse.today.droppingsCount ?? 0}</dd></div>
              <div><dt>Exercise</dt><dd>{horse.today.exercise?.mins ? `${horse.today.exercise.mins} min ${horse.today.exercise.intensity || ""}` : "Rest day"}</dd></div>
            </dl>
            {horse.today.notes && <p className="v6-notes">"{horse.today.notes}"</p>}
          </section>
        )}

        <div className="v6-actions">
          <button className="btn ghost" onClick={() => navigate(`/horses/${horse.id}`)}>
            View full profile
          </button>
          <button className="btn primary" onClick={() => navigate(`/horses/${horse.id}/log`)}>
            Log update →
          </button>
        </div>

        <div className="v6-sheet-foot">
          <span>← → to scroll between horses · Esc to close</span>
        </div>
      </div>
    </div>
  );
}

function V6Top({ initial, email }: { initial: string; email: string | null }) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  // Click-outside closes the menu.
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".v6-user-menu-wrap")) setMenuOpen(false);
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <div className="v6-top">
      <Link to="/dashboard" className="v6-brand" style={{ textDecoration: "none", color: "inherit" }}>
        <img src="/logo-wordmark.png" alt="Troughmate" height={22} style={{ display: "block", width: "auto" }} />
      </Link>
      <div className="v6-top-right">
        <button className="btn ghost sm" onClick={() => navigate("/horses/new")}>+ Add horse</button>
        <Link to="/settings" className="v6-icon" title="Settings" aria-label="Settings">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </Link>
        <div className="v6-user-menu-wrap" style={{ position: "relative" }}>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Account menu"
            aria-expanded={menuOpen}
            style={{
              background: "transparent",
              border: 0,
              padding: 0,
              cursor: "pointer",
              borderRadius: "50%",
            }}
          >
            <span className="avatar" style={{ background: "#0E5E5A", width: 30, height: 30, fontSize: 11 }}>
              {initial}
            </span>
          </button>
          {menuOpen && (
            <div
              role="menu"
              style={{
                position: "absolute",
                top: "calc(100% + 6px)",
                right: 0,
                background: "var(--surface)",
                border: "1px solid var(--line)",
                borderRadius: "var(--r-md)",
                boxShadow: "var(--shadow-pop)",
                minWidth: 200,
                padding: 6,
                zIndex: 50,
              }}
            >
              {email && (
                <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--ink-3)", borderBottom: "1px solid var(--line)" }}>
                  {email}
                </div>
              )}
              <button
                type="button"
                onClick={() => { setMenuOpen(false); navigate("/settings"); }}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  background: "transparent",
                  border: 0,
                  padding: "10px 12px",
                  fontSize: 13,
                  color: "var(--ink)",
                  cursor: "pointer",
                  borderRadius: "var(--r-sm)",
                }}
              >
                Settings
              </button>
              <button
                type="button"
                onClick={handleSignOut}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  background: "transparent",
                  border: 0,
                  padding: "10px 12px",
                  fontSize: 13,
                  color: "var(--status-severe)",
                  cursor: "pointer",
                  borderRadius: "var(--r-sm)",
                }}
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyYard({ name }: { name: string }) {
  const navigate = useNavigate();
  return (
    <main className="v6-main">
      <header className="v6-greet">
        <h1>Welcome, {name}.</h1>
      </header>
      <div
        style={{
          background: "var(--surface)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-lg)",
          padding: "56px 32px",
          textAlign: "center",
          boxShadow: "var(--shadow-card)",
          marginTop: 20,
        }}
      >
        <div style={{ fontSize: 44, marginBottom: 14, opacity: 0.4 }} aria-hidden="true">🐴</div>
        <h2 style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em" }}>
          Your yard is empty
        </h2>
        <p style={{ margin: "0 auto 22px", color: "var(--ink-3)", fontSize: 14, maxWidth: 380, lineHeight: 1.5 }}>
          Add your first horse to start tracking hydration. The Equine Hydration
          Estimator works the moment you log your first day.
        </p>
        <button className="btn primary lg" onClick={() => navigate("/horses/new")}>
          + Add a horse
        </button>
      </div>
    </main>
  );
}

export default function Dashboard() {
  const isMobile = useIsMobile();
  const { user, isDemo } = useCurrentUser();
  const { yard, loading: yardLoading } = useUserYard(user?.uid);
  const { horses: realHorses, loading: horsesLoading } = useHorses(yard?.id);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const enriched: EnrichedHorse[] = useMemo(() => {
    const source: MockHorse[] = isDemo ? MOCK_HORSES : realHorses.map(horseToMock);
    return source.map((h) => {
      const s = getCardState(h);
      return { ...h, _state: s.state, _pace: s.pace, _fresh: s.fresh, _hasFlags: s.hasFlags };
    });
  }, [isDemo, realHorses]);

  const ordered = useMemo(() => {
    const order: Record<CardState, number> = { severe: 0, behind: 1, stale: 2, ok: 3 };
    return [...enriched].sort(
      (a, b) => order[a._state] - order[b._state] || a.name.localeCompare(b.name)
    );
  }, [enriched]);

  const idx = selectedId ? ordered.findIndex((h) => h.id === selectedId) : -1;
  const selectedHorse = idx >= 0 ? ordered[idx]! : null;
  const goPrev = useCallback(() => {
    if (idx < 0) return;
    setSelectedId(ordered[(idx - 1 + ordered.length) % ordered.length]!.id);
  }, [idx, ordered]);
  const goNext = useCallback(() => {
    if (idx < 0) return;
    setSelectedId(ordered[(idx + 1) % ordered.length]!.id);
  }, [idx, ordered]);

  const name = isDemo ? MOCK_YARD.manager : firstName(user);
  const initial = (name[0] || "?").toUpperCase();
  const isLoadingReal = !isDemo && (yardLoading || horsesLoading);

  if (isLoadingReal) {
    return (
      <div className={`v6 ${isMobile ? "is-mobile" : "is-desktop"}`}>
        {user && <EmailVerifyBanner user={user} />}
        <V6Top initial={initial} email={user?.email ?? null} />
        <div className="v6-page">
          <main className="v6-main">
            <div style={{ padding: "60px 0", textAlign: "center", color: "var(--ink-3)", fontSize: 14 }}>
              Loading your yard…
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (enriched.length === 0) {
    return (
      <div className={`v6 ${isMobile ? "is-mobile" : "is-desktop"}`}>
        {user && <EmailVerifyBanner user={user} />}
        <V6Top initial={initial} email={user?.email ?? null} />
        <div className="v6-page">
          <EmptyYard name={name} />
        </div>
      </div>
    );
  }

  return (
    <div className={`v6 ${isMobile ? "is-mobile" : "is-desktop"}`}>
      {user && <EmailVerifyBanner user={user} />}
      <V6Top initial={initial} email={user?.email ?? null} />

      <div className="v6-page">
        <main className="v6-main">
          <header className="v6-greet">
            <h1>{greetingFor(new Date())}, {name}</h1>
          </header>

          <div className={`v6-grid ${isMobile ? "cols-1" : "cols-3"}`}>
            {ordered.map((h) => (
              <HorseCard key={h.id} horse={h} onOpen={(id) => setSelectedId(id)} />
            ))}
          </div>
        </main>

        {!isMobile && <YardColumn enriched={enriched} onPickHorse={(id) => setSelectedId(id)} />}
      </div>

      {isMobile && (
        <section className="v6-mobile-summary">
          <YardColumn enriched={enriched} onPickHorse={(id) => setSelectedId(id)} />
        </section>
      )}

      {selectedHorse && (
        <DetailOverlay
          horse={selectedHorse}
          onClose={() => setSelectedId(null)}
          onPrev={goPrev}
          onNext={goNext}
        />
      )}
    </div>
  );
}
